/**
 * /activate-account — first-time member activation flow.
 *
 * The destination when a member returns from completing checkout on Shopify
 * (or any other surface that wants to land them on their new archive
 * credentials).
 *
 * Two states it handles:
 *  1. First-time arrival with ?email=<paid-customer-email> AND the backend
 *     confirms the account needs a password → cinematic "Activate your archive
 *     access" panel.
 *  2. Account is already claimed / no email param → redirected to standard
 *     sign-in, which preserves the returning-user flow.
 *
 *  After credentials are created we hydrate auth context, then the page renders
 *  the calm "Your archive access is now active" greeter with resume + suggested
 *  threads. Onboarding quiz is still triggered by DashboardPage on next visit
 *  (architecture preserved).
 *
 *  Mounted on both `/activate-account` (new canonical path) and `/welcome-back`
 *  (legacy alias for any URLs already in the wild from earlier emails / shares).
 *
 * Never shows a hard error; never breaks the post-checkout emotional beat.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import SubscriptionStatusBanner from "../components/SubscriptionStatusBanner";
import LogoMark from "../components/LogoMark";
import api from "../lib/api";
import { ArrowRight, Loader2, ShieldCheck, Eye, EyeOff } from "lucide-react";

const POLL_INTERVAL_MS = 4000;
const POLL_MAX_MS = 40000;

// Subscription states that indicate the webhook DID land but the result is
// not "open archive" — we should stop polling and show the grace banner
// instead of looping forever.
const TERMINAL_NON_MEMBER_STATUSES = new Set([
    "failed", "cancelled", "expired", "paused",
]);

// Canonical activation route. We compute it from location so legacy
// /welcome-back URLs preserve themselves through the sign-in redirect.
function canonicalActivationPath(pathname) {
    return pathname && pathname.startsWith("/welcome-back") ? "/welcome-back" : "/activate-account";
}

export default function ActivateAccountPage() {
    const { user, refresh, checking, hydrateFromCustomerLogin } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [pollElapsed, setPollElapsed] = useState(0);
    const [resume, setResume] = useState(null);   // last viewed topic
    const [suggested, setSuggested] = useState([]);
    const pollTimer = useRef(null);

    // Claim flow state — for first-time Shopify members who don't have a TCP
    // password yet. Decided in the redirect effect below.
    const [claimEmail, setClaimEmail] = useState(null);
    const [claimChecking, setClaimChecking] = useState(true);
    const [claimPassword, setClaimPassword] = useState("");
    const [claimPassword2, setClaimPassword2] = useState("");
    const [claimShowPw, setClaimShowPw] = useState(false);
    const [claimSubmitting, setClaimSubmitting] = useState(false);
    const [claimError, setClaimError] = useState("");

    // Webhook-race state — when Shopify redirects the new customer to
    // /activate-account BEFORE the customers/update webhook has landed and
    // created the TCP user stub. The cinematic finalising panel surfaces
    // while we poll /auth/claim-status briefly.
    const [pendingSync, setPendingSync] = useState(false);
    const [pendingEmail, setPendingEmail] = useState(null);
    const syncTimer = useRef(null);

    // Email-input branch — for members who land on /activate-account WITHOUT
    // a ?email= query string (Shopify wasn't configured to pass it, or the
    // user navigated here from a saved/shared link). Instead of bouncing
    // them to /account/login (where they have nothing to sign in with), we
    // ask for the email they used at checkout right here.
    const [showEmailInput, setShowEmailInput] = useState(false);
    const [emailInput, setEmailInput] = useState("");
    const [emailExistingClaimed, setEmailExistingClaimed] = useState(false);
    const [emailLookupSubmitting, setEmailLookupSubmitting] = useState(false);
    const [emailLookupError, setEmailLookupError] = useState("");

    const returnPath = canonicalActivationPath(location.pathname);

    // Gate: signed-out users either go to the cinematic activation flow (if
    // arriving from Shopify with ?email=…) or to standard sign-in.
    useEffect(() => {
        if (checking) return;
        if (user) {
            // Authed — skip the activation flow entirely.
            setClaimChecking(false);
            return;
        }
        const params = new URLSearchParams(location.search);
        const emailParam = (params.get("email") || "").toLowerCase().trim();
        if (!emailParam) {
            // No email in the URL — show the cinematic activation form with
            // an email field. This is the path real Shopify customers take
            // when the storefront isn't templating {{customer.email}} into
            // the redirect URL.
            setShowEmailInput(true);
            setClaimChecking(false);
            return;
        }

        // Webhook-race-aware claim-status check. Three outcomes:
        //   needs_password: true             → show claim panel
        //   exists: false                    → webhook lag; poll for ≤40s
        //   exists: true && !needs_password  → returning member → sign-in
        let cancelled = false;
        const start = Date.now();
        const POLL_INTERVAL = 3000;
        const POLL_MAX = 40000;

        const check = async () => {
            try {
                const r = await api.get("/auth/claim-status", { params: { email: emailParam } });
                if (cancelled) return;
                const d = r.data || {};
                if (d.needs_password) {
                    // Stub landed → activation form opens.
                    setClaimEmail(emailParam);
                    setPendingSync(false);
                    setPendingEmail(null);
                    setClaimChecking(false);
                    return;
                }
                if (d.exists) {
                    // Returning customer with a password already set.
                    navigate(`/account/login?next=${returnPath}&email=${encodeURIComponent(emailParam)}`);
                    return;
                }
                // exists === false → Shopify webhook hasn't fired yet.
                // Surface the cinematic finalising panel and keep polling.
                setPendingEmail(emailParam);
                setPendingSync(true);
                setClaimChecking(false);
                if (Date.now() - start < POLL_MAX) {
                    syncTimer.current = window.setTimeout(check, POLL_INTERVAL);
                } else {
                    // Give up quietly — pre-fill sign-in with email so a
                    // returning visitor or a slow webhook still resolves.
                    navigate(`/account/login?next=${returnPath}&email=${encodeURIComponent(emailParam)}`);
                }
            } catch {
                if (cancelled) return;
                navigate(`/account/login?next=${returnPath}`);
            }
        };
        check();

        return () => {
            cancelled = true;
            if (syncTimer.current) window.clearTimeout(syncTimer.current);
        };
    }, [user, checking, navigate, location.search, returnPath]);

    async function submitClaim(e) {
        e.preventDefault();
        setClaimError("");
        if (claimPassword.length < 10) {
            setClaimError("Choose a password of at least 10 characters.");
            return;
        }
        if (claimPassword !== claimPassword2) {
            setClaimError("Passwords don't match.");
            return;
        }
        setClaimSubmitting(true);
        try {
            const r = await api.post("/auth/claim", { email: claimEmail, password: claimPassword });
            // Hydrate auth context immediately so the rest of the page renders
            // the cinematic "access is active" branch without an extra round-trip.
            // Pass the Bearer token for the iOS Safari ITP localStorage fallback.
            if (r.data?.user) hydrateFromCustomerLogin(r.data.user, r.data.token);
            await refresh();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setClaimError(typeof detail === "string" ? detail : "Unable to set up your account. Please try again.");
        } finally {
            setClaimSubmitting(false);
        }
    }

    // STATE 0b activation submit — full email + password form. Posts directly
    // to /auth/claim, which is idempotent during beta: it creates the member
    // record if missing, claims a pending-webhook stub if found, or returns
    // 409 if the email is already activated.
    async function submitActivation(e) {
        e.preventDefault();
        setEmailLookupError("");
        setEmailExistingClaimed(false);
        const cleanEmail = emailInput.toLowerCase().trim();
        if (!cleanEmail || !cleanEmail.includes("@") || cleanEmail.length < 5) {
            setEmailLookupError("Please enter the email you used at checkout.");
            return;
        }
        if (claimPassword.length < 10) {
            setEmailLookupError("Choose a password of at least 10 characters.");
            return;
        }
        if (claimPassword !== claimPassword2) {
            setEmailLookupError("Passwords don't match.");
            return;
        }
        setEmailLookupSubmitting(true);
        try {
            const r = await api.post("/auth/claim", { email: cleanEmail, password: claimPassword });
            if (r.data?.user) hydrateFromCustomerLogin(r.data.user, r.data.token);
            await refresh();
            // Auth context is now populated — gate render below transitions to
            // STATE 2 (cinematic "you're in" greeter). No navigation needed.
        } catch (err) {
            const status = err?.response?.status;
            if (status === 409) {
                setEmailExistingClaimed(true);
            } else if (status === 429) {
                setEmailLookupError("Too many attempts. Please wait a few minutes and try again.");
            } else {
                const detail = err?.response?.data?.detail;
                setEmailLookupError(typeof detail === "string" ? detail : "Unable to set up your account. Please try again.");
            }
        } finally {
            setEmailLookupSubmitting(false);
        }
    }

    // Poll for the member tag to land if it hasn't yet.
    useEffect(() => {
        if (!user) return undefined;
        if (user.is_member) return undefined;
        // If Shopify already reported a terminal non-active state, polling
        // is pointless — the grace banner below tells the truth calmly.
        const status = (user.subscription_status || "").toLowerCase();
        if (TERMINAL_NON_MEMBER_STATUSES.has(status)) return undefined;
        // Already signed in but not yet a member → webhook lag.
        const start = Date.now();
        const tick = async () => {
            const elapsed = Date.now() - start;
            setPollElapsed(elapsed);
            if (elapsed >= POLL_MAX_MS) {
                // Give up quietly — the user can still browse; the dashboard
                // will show the right state.
                return;
            }
            await refresh();
            pollTimer.current = window.setTimeout(tick, POLL_INTERVAL_MS);
        };
        pollTimer.current = window.setTimeout(tick, POLL_INTERVAL_MS);
        return () => {
            if (pollTimer.current) window.clearTimeout(pollTimer.current);
        };
    }, [user, refresh]);

    // Once we know they're a member, fetch the personalised payload.
    useEffect(() => {
        if (!user?.is_member) return;
        let cancelled = false;
        Promise.all([
            api.get("/me/continue").catch(() => ({ data: [] })),
            api.get("/me/suggested").catch(() => ({ data: [] })),
        ]).then(([c, s]) => {
            if (cancelled) return;
            const items = c.data || [];
            setResume(items[0] || null);
            setSuggested((s.data || []).slice(0, 3));
        });
        return () => { cancelled = true; };
    }, [user?.is_member]);

    if (checking || claimChecking) {
        return (
            <div className="min-h-screen bg-[#0A0F1A] text-[#94A3B8] flex items-center justify-center text-sm">
                Opening the archive…
            </div>
        );
    }

    // STATE 0b — Email-input form: user arrived at /activate-account WITHOUT
    // a ?email= query string (Shopify post-checkout redirect wasn't templated
    // with {{customer.email}}, or the user navigated here manually). We ask
    // for the email they used at checkout and route them through the rest of
    // the activation chain. Never bounces to /account/login.
    if (!user && showEmailInput) {
        return (
            <div className="min-h-screen bg-[#0A0F1A] text-white">
                <Header />
                <main className="max-w-xl mx-auto px-6 sm:px-8 py-20 sm:py-28" data-testid="activate-account-email-input">
                    <div className="mb-10">
                        <LogoMark size="sm" dataTestId="activate-email-logo" />
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#56CCF2] font-medium mb-6">
                        Activate the archive
                    </div>
                    <h1 className="text-3xl sm:text-[40px] font-bold tracking-tight leading-[1.05] mb-5">
                        Create your archive credentials.
                    </h1>
                    <p className="text-[15px] text-[#94A3B8] leading-relaxed mb-3 max-w-md">
                        You're creating the email and password you'll use to access the archive platform from now on.
                    </p>
                    <p className="text-[13px] text-[#64748B] leading-relaxed mb-10 max-w-md">
                        Use the same email address you used at checkout.
                    </p>

                    <form onSubmit={submitActivation} className="space-y-5">
                        <div>
                            <label className="block text-[11px] uppercase tracking-[0.22em] text-[#64748B] font-medium mb-2">
                                Email address
                            </label>
                            <input
                                data-testid="activate-email-input"
                                type="email"
                                autoComplete="email"
                                autoFocus
                                value={emailInput}
                                onChange={(e) => { setEmailInput(e.target.value); setEmailExistingClaimed(false); setEmailLookupError(""); }}
                                required
                                className="w-full px-4 py-3 rounded-md bg-[#0B1220] border border-[#1A2233] text-[15px] text-white placeholder:text-[#475569] focus:outline-none focus:border-[#56CCF2]/50 transition-colors"
                                placeholder="you@example.com"
                            />
                        </div>

                        <div>
                            <label className="block text-[11px] uppercase tracking-[0.22em] text-[#64748B] font-medium mb-2">
                                Create a password
                            </label>
                            <div className="relative">
                                <input
                                    data-testid="activate-password"
                                    type={claimShowPw ? "text" : "password"}
                                    autoComplete="new-password"
                                    value={claimPassword}
                                    onChange={(e) => setClaimPassword(e.target.value)}
                                    minLength={10}
                                    maxLength={72}
                                    required
                                    className="w-full px-4 py-3 pr-11 rounded-md bg-[#0B1220] border border-[#1A2233] text-[15px] text-white placeholder:text-[#475569] focus:outline-none focus:border-[#56CCF2]/50 transition-colors"
                                    placeholder="At least 10 characters"
                                />
                                <button
                                    type="button"
                                    onClick={() => setClaimShowPw((v) => !v)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-[#64748B] hover:text-[#94A3B8]"
                                    aria-label={claimShowPw ? "Hide password" : "Show password"}
                                    tabIndex={-1}
                                >
                                    {claimShowPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="block text-[11px] uppercase tracking-[0.22em] text-[#64748B] font-medium mb-2">
                                Confirm password
                            </label>
                            <input
                                data-testid="activate-password-confirm"
                                type={claimShowPw ? "text" : "password"}
                                autoComplete="new-password"
                                value={claimPassword2}
                                onChange={(e) => setClaimPassword2(e.target.value)}
                                minLength={10}
                                maxLength={72}
                                required
                                className="w-full px-4 py-3 rounded-md bg-[#0B1220] border border-[#1A2233] text-[15px] text-white placeholder:text-[#475569] focus:outline-none focus:border-[#56CCF2]/50 transition-colors"
                                placeholder="Re-enter the same password"
                            />
                        </div>

                        {emailExistingClaimed ? (
                            <div
                                data-testid="activate-email-already-claimed"
                                className="text-[13px] text-[#94A3B8] leading-relaxed bg-[#0B1220] border border-[#243044] rounded-md px-4 py-3"
                            >
                                An account already exists for this email.{" "}
                                <Link
                                    to={`/account/login?email=${encodeURIComponent(emailInput.toLowerCase().trim())}`}
                                    className="text-[#56CCF2] hover:text-white font-medium inline-flex items-center gap-1"
                                    data-testid="activate-email-signin-link"
                                >
                                    Sign in <ArrowRight className="w-3.5 h-3.5" />
                                </Link>
                            </div>
                        ) : null}

                        {emailLookupError ? (
                            <div
                                data-testid="activate-email-error"
                                className="text-[13px] text-[#E0B341] leading-relaxed"
                            >
                                {emailLookupError}
                            </div>
                        ) : null}

                        <button
                            data-testid="activate-email-submit"
                            type="submit"
                            disabled={emailLookupSubmitting}
                            className="group inline-flex items-center gap-2 mt-3 px-6 py-3 rounded-full bg-[#56CCF2] text-[#0B1220] text-sm font-semibold tracking-wide hover:bg-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {emailLookupSubmitting ? (
                                <><Loader2 className="w-4 h-4 animate-spin" /> Activating your access…</>
                            ) : (
                                <>Activate the archive <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" /></>
                            )}
                        </button>
                    </form>

                    <div className="mt-12 pt-6 border-t border-[#1A2233] text-[12px] text-[#64748B] leading-relaxed">
                        <ShieldCheck className="w-3.5 h-3.5 inline mr-1.5 -mt-px text-[#56CCF2]/60" />
                        Archive credentials are stored only by The Context Project — never shared with Shopify or billing.
                    </div>
                </main>
            </div>
        );
    }

    // STATE 0a — Webhook-race: Shopify customer arrived before the
    // customers/update webhook landed. Cinematic finalising panel sits
    // here while we keep polling claim-status in the background (set up
    // by the gate effect above). Never redirects to login.
    if (!user && pendingSync) {
        return (
            <div className="min-h-screen bg-[#0A0F1A] text-white">
                <Header />
                <main className="max-w-2xl mx-auto px-6 sm:px-8 py-20 sm:py-28">
                    <div data-testid="activate-account-syncing">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[#56CCF2] font-medium mb-6 inline-flex items-center gap-2">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Finalising your activation
                        </div>
                        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight mb-5">
                            Just a moment.
                        </h1>
                        <p className="text-[15px] text-[#94A3B8] leading-relaxed max-w-xl mb-3">
                            Your purchase came through. We're syncing your account with Shopify — your activation step will open in a few seconds.
                        </p>
                        {pendingEmail ? (
                            <p
                                className="text-[12px] text-[#64748B] font-mono mt-6"
                                data-testid="activate-account-syncing-email"
                            >
                                {pendingEmail}
                            </p>
                        ) : null}
                    </div>
                </main>
            </div>
        );
    }

    // STATE 0 — Cinematic "Activate your archive access" for first-time
    // Shopify members. Reached when ?email=<paid-customer-email> is present
    // AND the backend confirms `pending_claim=true` on that account.
    if (!user && claimEmail) {
        return (
            <div className="min-h-screen bg-[#0A0F1A] text-white">
                <Header />
                <main className="max-w-xl mx-auto px-6 sm:px-8 py-20 sm:py-28" data-testid="activate-account-claim">
                    <div className="mb-10">
                        <LogoMark size="sm" dataTestId="claim-logo" />
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#56CCF2] font-medium mb-6">
                        Your purchase is complete
                    </div>
                    <h1 className="text-3xl sm:text-[40px] font-bold tracking-tight leading-[1.05] mb-5">
                        Activate your archive access.
                    </h1>
                    <p className="text-[15px] text-[#94A3B8] leading-relaxed mb-3 max-w-md">
                        Your membership is live. Create the password you'll use to access the archive platform from now on.
                    </p>
                    <p className="text-[13px] text-[#64748B] leading-relaxed mb-10 max-w-md">
                        You'll sign in with this email and password each time you return.
                    </p>

                    <form onSubmit={submitClaim} className="space-y-5">
                        <div>
                            <label className="block text-[11px] uppercase tracking-[0.22em] text-[#64748B] font-medium mb-2">
                                Account email
                            </label>
                            <div
                                data-testid="claim-email"
                                className="px-4 py-3 rounded-md bg-[#0B1220] border border-[#1A2233] text-[14px] text-[#94A3B8] font-mono"
                            >
                                {claimEmail}
                            </div>
                        </div>

                        <div>
                            <label className="block text-[11px] uppercase tracking-[0.22em] text-[#64748B] font-medium mb-2">
                                Create a password
                            </label>
                            <div className="relative">
                                <input
                                    data-testid="claim-password"
                                    type={claimShowPw ? "text" : "password"}
                                    autoComplete="new-password"
                                    autoFocus
                                    value={claimPassword}
                                    onChange={(e) => setClaimPassword(e.target.value)}
                                    minLength={10}
                                    maxLength={72}
                                    required
                                    className="w-full px-4 py-3 pr-11 rounded-md bg-[#0B1220] border border-[#1A2233] text-[15px] text-white placeholder:text-[#475569] focus:outline-none focus:border-[#56CCF2]/50 transition-colors"
                                    placeholder="At least 10 characters"
                                />
                                <button
                                    type="button"
                                    onClick={() => setClaimShowPw((v) => !v)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-[#64748B] hover:text-[#94A3B8]"
                                    aria-label={claimShowPw ? "Hide password" : "Show password"}
                                    tabIndex={-1}
                                >
                                    {claimShowPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="block text-[11px] uppercase tracking-[0.22em] text-[#64748B] font-medium mb-2">
                                Confirm password
                            </label>
                            <input
                                data-testid="claim-password-confirm"
                                type={claimShowPw ? "text" : "password"}
                                autoComplete="new-password"
                                value={claimPassword2}
                                onChange={(e) => setClaimPassword2(e.target.value)}
                                minLength={10}
                                maxLength={72}
                                required
                                className="w-full px-4 py-3 rounded-md bg-[#0B1220] border border-[#1A2233] text-[15px] text-white placeholder:text-[#475569] focus:outline-none focus:border-[#56CCF2]/50 transition-colors"
                                placeholder="Re-enter the same password"
                            />
                        </div>

                        {claimError ? (
                            <div
                                data-testid="claim-error"
                                className="text-[13px] text-[#E0B341] leading-relaxed"
                            >
                                {claimError}
                            </div>
                        ) : null}

                        <button
                            data-testid="claim-submit"
                            type="submit"
                            disabled={claimSubmitting}
                            className="group inline-flex items-center gap-2 mt-3 px-6 py-3 rounded-full bg-[#56CCF2] text-[#0B1220] text-sm font-semibold tracking-wide hover:bg-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {claimSubmitting ? (
                                <><Loader2 className="w-4 h-4 animate-spin" /> Activating your access…</>
                            ) : (
                                <>Activate the archive <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" /></>
                            )}
                        </button>
                    </form>

                    <div className="mt-12 pt-6 border-t border-[#1A2233] text-[12px] text-[#64748B] leading-relaxed">
                        <ShieldCheck className="w-3.5 h-3.5 inline mr-1.5 -mt-px text-[#56CCF2]/60" />
                        Your password is stored only in the TCP archive — never shared with Shopify or billing.
                    </div>
                </main>
            </div>
        );
    }

    // No user, no email → effect already redirected to /account/login.
    if (!user) {
        return (
            <div className="min-h-screen bg-[#0A0F1A] text-[#94A3B8] flex items-center justify-center text-sm">
                Opening the archive…
            </div>
        );
    }

    // STATE 1a — terminal non-member status (failed/cancelled/paused/expired)
    // Webhook landed; we just don't have an active member. Show the grace
    // banner with the appropriate calm action, not the polling loop.
    const status = (user.subscription_status || "").toLowerCase();
    if (!user.is_member && TERMINAL_NON_MEMBER_STATUSES.has(status)) {
        return (
            <div className="min-h-screen bg-[#0A0F1A] text-white">
                <Header />
                <main className="max-w-2xl mx-auto px-6 sm:px-8 py-20 sm:py-28">
                    <div data-testid="activate-account-grace">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[#94A3B8] font-medium mb-6">
                            Welcome back
                        </div>
                        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight mb-5">
                            A small thing to handle first.
                        </h1>
                        <p className="text-[15px] text-[#94A3B8] leading-relaxed max-w-xl mb-8">
                            Your account is here — but the deeper archive is waiting on one quick step.
                        </p>
                        <SubscriptionStatusBanner user={user} />
                        <div className="mt-10 flex flex-wrap items-center gap-5">
                            <Link
                                to="/"
                                className="inline-flex items-center gap-2 text-sm text-[#94A3B8] hover:text-white"
                                data-testid="activate-account-grace-browse"
                            >
                                Keep browsing the public library <ArrowRight className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/dashboard"
                                className="inline-flex items-center gap-2 text-sm text-[#56CCF2] hover:text-white"
                                data-testid="activate-account-grace-workspace"
                            >
                                Open your workspace <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    // STATE 1 — finalising (webhook lag)
    if (!user.is_member) {
        const stillWaiting = pollElapsed < POLL_MAX_MS;
        return (
            <div className="min-h-screen bg-[#0A0F1A] text-white">
                <Header />
                <main className="max-w-2xl mx-auto px-6 sm:px-8 py-20 sm:py-28">
                    <div data-testid="activate-account-finalising">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[#56CCF2] font-medium mb-6 inline-flex items-center gap-2">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Finalising your activation
                        </div>
                        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight mb-5">
                            {stillWaiting ? "Almost in." : "Nearly there."}
                        </h1>
                        <p className="text-[15px] text-[#94A3B8] leading-relaxed max-w-xl">
                            {stillWaiting
                                ? "We're syncing your account with Shopify. The archive should open in a few seconds."
                                : "Your sync is taking a moment longer than usual. You can keep browsing — the archive will open as soon as it lands."}
                        </p>
                        <div className="mt-10">
                            <Link
                                to="/"
                                className="inline-flex items-center gap-2 text-sm text-[#56CCF2] hover:text-white"
                                data-testid="activate-account-keep-browsing"
                            >
                                Keep browsing for now <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    // STATE 2 — fully in, cinematic greeter
    return (
        <div className="min-h-screen bg-[#0A0F1A] text-white">
            <Header />
            <main className="max-w-3xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
                <div data-testid="activate-account-in">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#56CCF2] font-medium mb-6 inline-flex items-center gap-2">
                        <ShieldCheck className="w-3 h-3" /> Archive access is now active
                    </div>
                    <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05] mb-5">
                        You're in.
                    </h1>
                    <p className="text-[15px] sm:text-[16px] text-[#94A3B8] leading-relaxed max-w-2xl mb-12">
                        The deeper layer is open. Pathways, sourced timelines, the source vault — they all unfold from here. Take your time.
                    </p>

                    {resume ? (
                        <section className="mb-12">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-medium mb-3">
                                Pick up where you left off
                            </div>
                            <Link
                                to={`/topic/${resume.slug}`}
                                data-testid="activate-account-resume"
                                className="block px-5 py-5 rounded-xl bg-[#0B1220] border border-[#243044] hover:border-[#2F80ED] hover:bg-[#111827] transition-all"
                            >
                                <div className="text-[10px] uppercase tracking-[0.2em] text-[#56CCF2] font-medium mb-2">
                                    {resume.category || "Investigation"}
                                </div>
                                <h3 className="text-lg font-semibold text-white leading-snug mb-1">{resume.title}</h3>
                                {resume.subtitle ? (
                                    <p className="text-[13px] leading-[1.6] text-[#94A3B8] line-clamp-2">{resume.subtitle}</p>
                                ) : null}
                            </Link>
                        </section>
                    ) : null}

                    {suggested.length > 0 ? (
                        <section className="mb-12">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-medium mb-3">
                                Threads worth following
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="activate-account-suggested">
                                {suggested.map((t) => (
                                    <Link
                                        key={t.slug}
                                        to={`/topic/${t.slug}`}
                                        className="group block px-4 py-4 rounded-xl bg-[#0B1220] border border-[#243044] hover:border-[#2F80ED] hover:bg-[#111827] transition-all"
                                    >
                                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] mb-2">{t.category}</div>
                                        <h4 className="text-sm font-semibold text-white leading-snug group-hover:text-[#56CCF2]">{t.title}</h4>
                                    </Link>
                                ))}
                            </div>
                        </section>
                    ) : null}

                    <div className="flex flex-wrap gap-4 pt-2">
                        <Link
                            to="/dashboard"
                            data-testid="activate-account-workspace"
                            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#56CCF2] text-[#0A0F1A] text-sm font-semibold hover:bg-white"
                        >
                            Open the workspace <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link
                            to="/"
                            className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm text-[#94A3B8] hover:text-white border border-[#243044]"
                        >
                            Back to the library
                        </Link>
                    </div>
                </div>
            </main>
        </div>
    );
}
